import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Package, Wrench, Broom, Desktop, Confetti, ArrowUpRight } from "@phosphor-icons/react";
import SectionEyebrow from "@/components/shared/SectionEyebrow";
import useSeo from "@/lib/useSeo";

const BOX_IMG = "https://static.prod-images.emergentagent.com/jobs/e1b22ba1-7cff-4c1a-be5f-f374ac624625/images/ff57732d7af862659cd7cd819f9ed7ed49635f7cf070d22bba986e62648b21db.png";

const SERVICES = [
  {
    icon: Package, color: "#FF7A45", id: "delivery", name: "Delivery",
    tagline: "Same-day delivery for food, groceries, parcels and documents — across Trinidad & Tobago.",
    bullets: ["Food & restaurants", "Groceries & convenience", "Parcels & courier", "Same-day documents"],
  },
  {
    icon: Wrench, color: "#14B8A6", id: "handyman", name: "Handyman",
    tagline: "On-demand plumbing, electrical, AC, repairs and odd jobs from verified local pros.",
    bullets: ["Plumbing & electrical", "AC & appliance repair", "General repairs", "Insured & vetted"],
  },
  {
    icon: Broom, color: "#D4AF37", id: "cleaning", name: "Cleaning",
    tagline: "Home, office and post-event cleaning with vetted, insured crews.",
    bullets: ["Residential cleaning", "Office & commercial", "Post-event clean-up", "Deep cleans"],
  },
  {
    icon: Desktop, color: "#5EEAD4", id: "tech-support", name: "Tech Support",
    tagline: "Computer repair, network setup, smart home installs and on-site troubleshooting.",
    bullets: ["PC & laptop repair", "Wi-Fi & networking", "Smart home setup", "On-site support"],
  },
  {
    icon: Confetti, color: "#FB7185", id: "events", name: "Events",
    tagline: "Bartenders, decorators, security and crew for parties, weddings and corporate events.",
    bullets: ["Bartending & catering", "Decor & rentals", "Event security", "Setup & breakdown crew"],
  },
];

export default function Services() {
  useSeo({
    title: "Services",
    description: "Delivery, handyman, cleaning, tech support and events — five on-demand services powered by IslandHop's Caribbean network of verified pros.",
  });
  return (
    <div data-testid="services-page">
      <section className="relative pt-20 lg:pt-28 pb-16 border-b border-ih-line overflow-hidden">
        <div className="absolute inset-0 ih-grid-bg opacity-30" />
        <div className="absolute -top-32 right-0 w-[600px] h-[600px] ih-radial-sunset" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 space-y-6">
            <SectionEyebrow label="Services" color="orange" />
            <h1 className="font-display font-black text-5xl lg:text-7xl text-white leading-[0.95] text-balance">
              Five services.<br />
              <span className="text-white/40">One</span> <span className="text-ih-sunset">trusted network.</span>
            </h1>
            <p className="text-white/70 text-lg max-w-xl leading-relaxed">
              From last-minute deliveries to weekend cleanings — IslandHop runs five on-demand services on a
              single platform, all powered by verified, locally-vetted Caribbean professionals.
            </p>
          </div>
          <div className="lg:col-span-5">
            <img src={BOX_IMG} alt="IslandHop multi-service package" className="w-full h-[360px] object-contain" />
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 space-y-10">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            const reverse = i % 2 === 1;
            return (
              <motion.div
                key={s.id}
                id={s.id}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.55 }}
                data-testid={`service-detail-${s.id}`}
                className={`grid lg:grid-cols-12 gap-8 items-center bg-ih-surface border border-ih-line rounded-3xl p-8 lg:p-12 ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}
              >
                <div className="lg:col-span-7 space-y-5">
                  <div className="flex items-center gap-3">
                    <span className="w-12 h-12 rounded-xl bg-black/40 border border-ih-line2 grid place-items-center" style={{ color: s.color }}>
                      <Icon weight="duotone" size={26} />
                    </span>
                    <span className="font-mono text-xs uppercase tracking-[0.18em] text-white/40">0{i + 1} / 0{SERVICES.length}</span>
                  </div>
                  <h2 className="font-display font-extrabold text-4xl lg:text-5xl text-white leading-[1.02]">{s.name}</h2>
                  <p className="text-white/65 text-lg max-w-xl leading-relaxed">{s.tagline}</p>
                  <ul className="grid sm:grid-cols-2 gap-2 pt-2 max-w-xl">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-center gap-2 text-sm text-white/70">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="lg:col-span-5">
                  <div className="aspect-square rounded-2xl border border-ih-line2 relative overflow-hidden ih-noise" style={{ background: `radial-gradient(60% 60% at 50% 50%, ${s.color}25, transparent 70%)` }}>
                    <div className="absolute inset-0 ih-grid-bg opacity-40" />
                    <Icon weight="duotone" size={120} className="absolute inset-0 m-auto opacity-90" style={{ color: s.color }} />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="py-20 border-t border-ih-line">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between">
          <h3 className="font-display font-extrabold text-3xl lg:text-4xl text-white max-w-xl">
            Have a service IslandHop should add? <span className="text-ih-teal">Tell us.</span>
          </h3>
          <Link to="/contact" data-testid="services-contact-cta" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-black font-semibold hover:scale-[1.02] transition">
            Get in touch <ArrowUpRight weight="bold" size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
